"use client";
import { formatIndianNumber } from "@/utils/commonFunctions";
import { motion } from "framer-motion";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import { ROOT_URL } from "@/utils/gloableVariables";

const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -60 : 60,
    opacity: 0,
  }),
};

type ComponentProps = {
  direction: number;
  selectedDate: number;
  onDateChange: (date: number) => void;
  amcCode?: string;
  schemeName?: string;
  tenure?: number;
  sipAmount?: number;
  mode?: "sip" | "lumpsum";
};

type InvestmentType = "sip" | "lumpsum";

interface InvestmentInput {
  amount?: number;
  type?: InvestmentType;
  tenure?: number;
  returns?: number;
}

interface InvestmentResult {
  invested: number;
  futureValue: number;
}

export default function InvestGoldFormStep3({
  direction,
  amcCode,
  schemeName,
  mode,
  sipAmount,
  tenure,
  selectedDate,
  onDateChange,
}: ComponentProps) {
  const [val, setVal] = useState<InvestmentResult | null>(null);

  useEffect(() => {
    const totalInvested = calculateInvestment({
      amount: sipAmount,
      type: mode,
      tenure,
    });
    setVal(totalInvested);
  }, [sipAmount, mode, tenure]);

  return (
    <motion.div
      key="step2"
      variants={stepVariants}
      custom={direction}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="w-full relative px-4 xs:px-6"
    >
      <div className="text-lg xs:text-xl font-medium text-white mb-0.5 md:mb-1">
        Final Summary
      </div>

      <p className="text-xs md:text-2xs text-white mb-3 md:mb-5 font-light">
        Note : This calculation is based on a 10% return over {tenure} years.
        Actual corpus may vary.
      </p>

      <div className="w-full max-w-4xl mx-auto pt-3 md:pt-0 md:rounded-2xl border-t md:border border-white/15 md:bg-white/5 text-white grid grid-cols-1 md:grid-cols-2">
        {/* Left Section */}
        <div className="md:p-4.5 md:border-r border-white/15">
          <p className="text-2sm leading-4 md:text-base md:leading-6 mb-2 md:mb-2.5">
            Selected Fund :
          </p>

          <div className="flex items-center gap-2 bg-white/15 px-3 py-2 md:p-4 rounded-4xl">
            <Image
              src={`${ROOT_URL}img/AMCsLogo/${amcCode}.png`}
              alt={schemeName || "Fund Icon"}
              width={28}
              height={28}
              className="w-6 h-6 md:w-7 md:h-7 overflow-hidden shrink-0 border border-[#E9E9EB] rounded-full"
            />

            <p className="text-sm md:text-base md:leading-6 leading-5 line-clamp-2">
              {schemeName}
            </p>
          </div>

          <div className="flex items-center justify-between xs:justify-start gap-2 xs:gap-8 mt-3.5 md:mt-10 text-xs md:text-2sm text-white">
            <div>
              <p>Investing as :</p>
              <p className="mt-1 text-2xs leading-4 md:text-sm md:leading-5 flex items-center gap-2 text-white/60">
                {mode === "sip" ? "Monthly SIP" : "Lumpsum"}
              </p>
            </div>

            <div>
              <p>{mode === "sip" ? "SIP" : "Lump."} Amount :</p>
              <p className="mt-1 text-2xs leading-4 md:text-sm md:leading-5 text-white/60">
                ₹ {formatIndianNumber(sipAmount || 0)}
              </p>
            </div>

            <div>
              <p>Tenure :</p>
              <p className="mt-1 text-2xs leading-4 md:text-sm md:leading-5 text-white/60">
                {tenure} Years
              </p>
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className="md:p-4.5 mt-4 md:mt-0">
          {mode === "sip" && (
            <>
              <p className="text-sm leading-4 md:text-lg md:leading-6.5 mb-2">
                Select SIP Date:
              </p>

              <div className="grid grid-cols-7 gap-1 md:gap-2 bg-transparent pb-2 md:p-3 border-b md:border border-white/15 md:rounded-xl text-center text-2xs">
                {Array.from({ length: 28 }).map((_, i) => {
                  const date = i + 1;
                  const isSelected = selectedDate === date;

                  return (
                    <button
                      key={date}
                      onClick={() => onDateChange(date)}
                      className={`w-7 h-7 flex items-center justify-center text-white rounded-full transition duration-300
                  ${isSelected ? "bg-[#C89900]" : "hover:bg-white/30"}`}
                    >
                      {date}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div
            className={`flex items-center justify-between gap-2 text-sm md:text-2sm text-white ${mode === "sip" && "mt-3.5 md:mt-10"}`}
          >
            <div>
              <p>Total Invested Amount :</p>
              <p className="mt-1 text-xs md:text-sm flex items-center gap-2 text-white/60">
                ₹ {formatIndianNumber(val?.invested || 0)}
              </p>
            </div>

            <div>
              <p>Expected Corpus :</p>
              <p className="mt-1 text-xs md:text-sm text-white/60">
                ₹ {formatIndianNumber(val?.futureValue || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function calculateInvestment({
  amount = 0,
  type = "sip",
  tenure = 5,
  returns = 15,
}: InvestmentInput): InvestmentResult {
  const rate = returns / 100;

  if (type === "lumpsum") {
    const futureValue = amount * Math.pow(1 + rate, tenure);

    return {
      invested: amount,
      futureValue,
    };
  }

  // SIP calculation
  const monthlyRate = rate / 12;
  const months = tenure * 12;

  const futureValue =
    amount *
    ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
    (1 + monthlyRate);

  return {
    invested: amount * months,
    futureValue,
  };
}
